<script setup>
import { computed, onMounted, ref, shallowRef, watch } from 'vue';
import apiFetch from '@wordpress/api-fetch';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import Filters from './components/Filters.vue';
import HorizontalBarChart from './components/HorizontalBarChart.vue';
import markerIconSource from './assets/marker-icon.svg?raw';
import { useProcurementsStore } from './stores/procurements';
import { useCountriesStore } from './stores/countries';
import { useCitiesStore } from './stores/cities';
import { useCityPopulationRangesStore } from './stores/cityPopulationRanges';
import { useProcurementTypesStore } from './stores/procurementTypes';
import { useBusinessModelTypologiesStore } from './stores/businessModelTypologies';
import { useGovernanceLevelsStore } from './stores/governanceLevels';
import { useRouteLoadingStore } from './stores/routeLoading';

const routeLoadingStore = useRouteLoadingStore();
const procurementsStore = useProcurementsStore();
const countriesStore = useCountriesStore();
const citiesStore = useCitiesStore();
const cityPopulationRangesStore = useCityPopulationRangesStore();
const procurementTypesStore = useProcurementTypesStore();
const businessModelTypologiesStore = useBusinessModelTypologiesStore();
const governanceLevelsStore = useGovernanceLevelsStore();

const strings = ref(null);
const googleMapsApiKey = ref('');
const isFiltersLoading = ref(false);
const chartsReady = ref(false);
const mapElement = ref(null);
const map = shallowRef(null);
let markers = [];
let markersBySlug = new Map();

// The marker asset's fill color is baked into the SVG, so building a grayed-out
// variant means swapping that color and re-encoding it as its own data URL
// rather than loading a second asset file.
const markerIconBlack = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(markerIconSource);
const markerIconGray = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(markerIconSource.replace('rgb(31, 31, 31)', '#9ca3af'));

const lang = document.documentElement.lang?.substring(0, 2);

const chartProcurementTypes = computed(() => {
	const active = procurementTypesStore.activeProcurementTypes;
	if (!active.length) return procurementTypesStore.procurementTypes;
	return procurementTypesStore.procurementTypes.filter(item => active.includes(item.slug));
});

const chartBusinessModelTypologies = computed(() => {
	const active = businessModelTypologiesStore.activeBusinessModelTypologies;
	if (!active.length) return businessModelTypologiesStore.businessModelTypologies;
	return businessModelTypologiesStore.businessModelTypologies.filter(item => active.includes(item.slug));
});

const hasNoChartData = computed(() => {
	return chartsReady.value
		&& !isFiltersLoading.value
		&& chartProcurementTypes.value.length === 0
		&& chartBusinessModelTypologies.value.length === 0
		&& governanceLevelsStore.governanceLevels.length === 0;
});

function renderMarkers() {
	if (!map.value) {
		return;
	}

	markers.forEach((marker) => marker.setMap(null));
	markers = [];
	markersBySlug = new Map();

	const bounds = new window.google.maps.LatLngBounds();
	const cityCounts = [];

	procurementsStore.procurements.forEach((procurement) => {
		const citySlug = procurement?.city?.slug;

		if (!citySlug) {
			return;
		}

		const cityEntry = cityCounts.find((item) => item.slug === citySlug);

		if (!cityEntry) {
			cityCounts.push({
				slug: citySlug,
				count: 1,
				name: procurement.city?.name,
				countryCode: procurement.city?.country_code,
			});
			return;
		}

		cityEntry.count += 1;
	});

	const cityCountMap = new Map(cityCounts.map((c) => [c.slug, c.count]));
	const hasSelectedCities = citiesStore.activeCities.length > 0;

	citiesStore.cities.forEach((city) => {
		const count = cityCountMap.get(city.slug);
		if (count === undefined) return;

		const latitude = Number.parseFloat(city.latitude);
		const longitude = Number.parseFloat(city.longitude);
		if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;

		const isSelected = citiesStore.activeCities.includes(city.slug);

		const marker = new window.google.maps.Marker({
			map: map.value,
			position: { lat: latitude, lng: longitude },
			icon: hasSelectedCities && !isSelected ? markerIconGray : markerIconBlack,
			label: {
				text: String(count),
				color: '#ededed',
				fontFamily: 'IBM Plex Mono',
				fontWeight: '600',
				fontSize: '18px',
			},
		});

		marker.addListener('click', () => citiesStore.toggleCity(city.slug));

		markers.push(marker);
		markersBySlug.set(city.slug, marker);
		bounds.extend({ lat: latitude, lng: longitude });
	});

	if (!markers.length) {
		map.value.setCenter({ lat: -14.235004, lng: -51.92528 });
		map.value.setZoom(3);
		return;
	}

	if (markers.length === 1) {
		map.value.setCenter(bounds.getCenter());
		map.value.setZoom(5);
		return;
	}

	map.value.fitBounds(bounds, 80);
}

// Selecting a pin only needs to recolor the existing markers, not rebuild
// them and refit the map bounds (which renderMarkers() does), otherwise
// every click would re-pan/re-zoom the map out from under the user.
function updateMarkerColors() {
	const hasSelectedCities = citiesStore.activeCities.length > 0;

	markersBySlug.forEach((marker, slug) => {
		const isSelected = citiesStore.activeCities.includes(slug);
		marker.setIcon(hasSelectedCities && !isSelected ? markerIconGray : markerIconBlack);
	});
}

async function initMap() {
	if (!googleMapsApiKey.value) {
		return;
	}

	try {
		setOptions({
			apiKey: googleMapsApiKey.value,
			version: 'weekly',
			language: lang,
		});

		const { Map } = await importLibrary('maps');

		map.value = new Map(mapElement.value, {
			center: { lat: -14.235004, lng: -51.92528 },
			zoom: 3,
			streetViewControl: false,
			mapTypeControl: false,
			zoomControl: true,
			// strictBounds must be true, not just the bounds themselves: it's what
			// actually clamps how far out the map can zoom (to "the world just
			// fits"), which is what stops it from tiling side by side. false (the
			// default) only limits panning and still allows zooming out past that.
			restriction: {
				latLngBounds: { north: 85, south: -85, west: -180, east: 180 },
				strictBounds: true,
			},
		});

		renderMarkers();
	} catch (error) {
		console.error('Error: ', error);
	}
}

onMounted(async () => {
	try {
		try {
			const translations = await apiFetch({
				path: `/wp-json/ebus/market/translations?lang=${lang}`,
			});
			const apiKey = await apiFetch({
				path: '/wp-json/ebus/market/google-maps-api-key',
			});

			strings.value = translations;
			googleMapsApiKey.value = apiKey?.api_key;
		} catch (error) {
			console.error('Error: ', error);
			strings.value = {};
		}

		await Promise.all([
			procurementsStore.loadProcurements({ lang }),
			procurementTypesStore.loadProcurementTypes({ lang }),
			businessModelTypologiesStore.loadBusinessModelTypologies({ lang }),
			governanceLevelsStore.loadGovernanceLevels({ lang }),
			countriesStore.loadCountries({ lang }),
			citiesStore.loadCities({ lang }),
			cityPopulationRangesStore.loadRanges({ lang }),
		]);

		chartsReady.value = true;
	} catch (error) {
		console.error('Error: ', error);
	} finally {
		routeLoadingStore.stop();
	}

	await initMap();
	renderMarkers();
});

watch(
	() => procurementsStore.procurements,
	() => {
		if (map.value) {
			renderMarkers();
		}
	},
);

function baseFilterParams() {
	return {
		lang,
		country: countriesStore.activeCountries,
		city_population_range: cityPopulationRangesStore.activeRanges,
		procurement_type: procurementTypesStore.activeProcurementTypes,
		business_model_typology: businessModelTypologiesStore.activeBusinessModelTypologies,
	};
}

async function reloadCharts() {
	isFiltersLoading.value = true;

	try {
		const chartParams = { ...baseFilterParams(), city: citiesStore.activeCities };

		await Promise.all([
			procurementTypesStore.loadProcurementTypes(chartParams),
			businessModelTypologiesStore.loadBusinessModelTypologies(chartParams),
			governanceLevelsStore.loadGovernanceLevels(chartParams),
		]);
	} catch (error) {
		console.error('Error: ', error);
	} finally {
		isFiltersLoading.value = false;
	}
}

// Selecting a city pin should narrow the analytics column without making the
// other pins disappear, so the map's own procurements query (which decides
// which cities get a pin) intentionally leaves out the city filter.
watch(
	() => [countriesStore.activeCountries, cityPopulationRangesStore.activeRanges, procurementTypesStore.activeProcurementTypes, businessModelTypologiesStore.activeBusinessModelTypologies],
	async () => {
		try {
			await procurementsStore.loadProcurements(baseFilterParams());
		} catch (error) {
			console.error('Error: ', error);
		}

		await reloadCharts();
	},
	{ deep: true },
);

watch(
	() => citiesStore.activeCities,
	() => {
		updateMarkerColors();
		reloadCharts();
	},
	{ deep: true },
);
</script>

<template>
	<div v-if="strings" class="w-full! min-h-screen! flex! flex-col! bg-light-blue-2!">
		<div class="w-full! bg-light-blue-2! flex! flex-col! lg:flex-row! items-end! pt-4! lg:pt-6! pb-6!">
			<h2 class="font-ibm-plex-mono! font-medium! text-4xl! leading-tight! text-black! w-full! lg:min-w-75! lg:max-w-75! px-4! lg:px-6!">
				{{ strings.dashboard }}
			</h2>		
			<div class="w-full! px-4! lg:px-0! py-4! lg:py-0!">
				{{ strings.dashboard_introduction }}
			</div>
			<div class="w-full! lg:min-w-76.5! lg:max-w-76.5!">
				<router-link
					to="/procurements"
					class="flex! items-center! gap-x-4.5! font-ibm-plex-mono! font-medium! text-lg! leading-tight! text-black! lowercase! no-underline!  px-4! lg:px-6!"
				>
					<span class="text-blue!">➞</span>
					{{ strings.all_procurements }}
				</router-link>
			</div>
		</div>

		<div class="w-full! flex! flex-col! lg:flex-row! lg:flex-1 isolate!">
			<div class="hidden lg:flex! flex-col! gap-y-4! w-full! lg:min-w-75! lg:max-w-75! h-auto! bg-light-blue-3! p-6!">
				<Filters :is-loading="isFiltersLoading" />
			</div>

			<div class="relative! w-full! h-100! lg:h-auto! bg-light-blue!">
				<div ref="mapElement" class="w-full! h-full!"></div>
			</div>

			<div class="relative! flex! flex-col! gap-y-5! w-full! lg:min-w-76.5! lg:max-w-76.5! h-auto! bg-light-blue-3! px-6! py-4!">
				<h3 class="font-ibm-plex-mono! font-medium! text-lg! leading-tight! text-black!">
					{{ strings.sample_analytics }}
				</h3>

				<div class="flex! flex-col! gap-y-5! pr-1!">
					<HorizontalBarChart
						:title="strings.procurement_type"
						:data="chartProcurementTypes"
						:is-column="true"
						:empty-message="hasNoChartData ? strings.no_procurements_match_filters : ''"
					/>

					<HorizontalBarChart
						:title="strings.business_model_typology"
						:data="chartBusinessModelTypologies"
						:is-column="true"
						:empty-message="hasNoChartData ? strings.no_procurements_match_filters : ''"
					/>

					<HorizontalBarChart
						:title="strings.governance_arrangement_level"
						:data="governanceLevelsStore.governanceLevels"
						:is-column="true"
						:empty-message="hasNoChartData ? strings.no_procurements_match_filters : ''"
					/>

					<router-link
						to="/analytics"
						class="mt-2.5! flex! items-center! gap-x-4.5! font-ibm-plex-mono! font-medium! text-lg! leading-tight! text-black! lowercase! no-underline!"
					>
					  <span class="text-blue">➞</span>
						{{ strings.more_analytics }}
					</router-link>
				</div>

				<div class="flex! justify-end! mt-auto!">
					<div class="flex! items-center! gap-x-5!">
						<router-link to="/references" class="font-ibm-plex-mono! font-medium! text-black! text-lg! leading-tight! no-underline!">
							{{ strings.references }}
						</router-link>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
